import { z } from "zod";

import { type DocumentType, DocumentTypeSchema } from "./schemas";

/**
 * Documents a task asks for. Each becomes one checklist row, answered by an
 * upload or by hand; either way the answer is stamped on `requirementProgress`.
 *
 * Whoever is being asked for a document must be able to read the request, so
 * every field is annotated: a `visibleTo: "*"` container defaults its
 * unannotated descendants to Admin-only.
 */

const requestedDocumentShape = {
  documentType: DocumentTypeSchema.describe("The document being asked for").meta({
    visibleTo: "*",
  }),
  label: z
    .string()
    .min(1)
    .max(120)
    .optional()
    .describe(
      "Free-text detail telling one request of a type from another; required for `other`, which is never proven by an upload",
    )
    .meta({ visibleTo: "*" }),
  acceptedDocumentTypes: z
    .array(DocumentTypeSchema)
    .min(1)
    .max(5)
    .optional()
    .describe("Document types that can satisfy this row; defaults to documentType")
    .meta({ visibleTo: "*" }),
  required: z
    .boolean()
    .optional()
    .describe("Whether the row blocks task completion; defaults to true")
    .meta({ visibleTo: "*" }),
};

const requestedDocumentKey = z
  .string()
  .min(1)
  .max(64)
  .describe("Stable per-entry id; suffixes the checklist requirement key and outlives label edits")
  .meta({ visibleTo: "*" });

const validateRequestedDocumentLabel = (
  {
    documentType,
    label,
    acceptedDocumentTypes,
  }: {
    documentType: DocumentType;
    label?: string;
    acceptedDocumentTypes?: DocumentType[];
  },
  ctx: z.RefinementCtx,
) => {
  if (documentType === "other" && !label?.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["label"],
      message: "A free-text description is required for `other`.",
    });
  }
  if (
    acceptedDocumentTypes &&
    new Set(acceptedDocumentTypes).size !== acceptedDocumentTypes.length
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["acceptedDocumentTypes"],
      message: "Accepted document types must be unique.",
    });
  }
};

/**
 * A document a task asks for. Echo an existing entry's `key` back to keep its
 * checklist state through a rewording; omit it and the API assigns a new one.
 */
const RequestedDocumentInputSchema = z
  .object({ key: requestedDocumentKey.optional(), ...requestedDocumentShape })
  .superRefine(validateRequestedDocumentLabel);
export type RequestedDocumentInput = z.infer<typeof RequestedDocumentInputSchema>;

export const RequestedDocumentSchema = z
  .object({
    key: requestedDocumentKey,
    ...requestedDocumentShape,
  })
  .superRefine(validateRequestedDocumentLabel);
export type RequestedDocument = z.infer<typeof RequestedDocumentSchema>;

export const requestedDocumentsField = z
  .array(RequestedDocumentInputSchema)
  .max(20)
  .optional()
  .describe("Documents to ask for; only accepted by task types that collect them");
